import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { type Column, ListTable, type ListState } from '@/components/admin/ListTable';
import {
  EMPTY_PEOPLE_FILTERS,
  MessagePeopleFilters,
  peopleFiltersToQuery,
  roleLabel,
  type MessagePeopleFilterValues,
} from '@/features/messages/MessagePeopleFilters';
import { fetchInbox, type InboxItemSummary } from '@/features/messages/api';
import { PhotoPreview } from '@/features/members/MemberPhotoField';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

/**
 * 共有受信箱（事務局専用）。受講生などから事務局への問い合わせ一覧（指示書 §5.1・§12 M8）。
 * 状態・優先度・担当者・SLAはUIから外し、単なる「差出人・最終更新」の一覧にした
 * （実機フィードバック「担当ってなんだ？SLAとかわかりにくいし。複雑な仕様にはしないで」
 * / D-168）。差出人はロール・期・コース・氏名で絞れる（D-356）。
 */
export function InboxPage(): ReactElement {
  const navigate = useNavigate();
  const [draftFilters, setDraftFilters] = useState<MessagePeopleFilterValues>(EMPTY_PEOPLE_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<MessagePeopleFilterValues>(EMPTY_PEOPLE_FILTERS);

  const query = useQuery({
    queryKey: ['inbox', appliedFilters],
    queryFn: () => fetchInbox(peopleFiltersToQuery(appliedFilters)),
  });

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  const columns: Column<InboxItemSummary>[] = [
    {
      key: 'requester',
      header: '差出人',
      primary: true,
      render: (row) => (
        <button
          type="button"
          onClick={() => navigate(`/inbox/${row.id}`)}
          className="flex items-center gap-2 text-left text-wpaccent hover:underline"
        >
          <PhotoPreview
            photoUrl={row.requester_photo_url}
            name={row.requester_name ?? '（送信者不明）'}
            size={28}
          />
          {row.requester_name ?? '（送信者不明）'}
        </button>
      ),
    },
    {
      key: 'requester_role',
      header: 'ロール',
      render: (row) => roleLabel(row.requester_role),
    },
    {
      key: 'member_link',
      header: '受講生詳細',
      render: (row) =>
        row.requester_member_id ? (
          <Link
            to={`/members/${row.requester_member_id}`}
            className="text-wpaccent hover:underline"
          >
            プロフィールを見る
          </Link>
        ) : (
          '—'
        ),
    },
    {
      key: 'last_message_at',
      header: '最終更新',
      render: (row) => (row.last_message_at ? formatDateTime(row.last_message_at) : '—'),
    },
  ];

  return (
    <>
      <PageHeader
        title="問い合わせ対応"
        description="受講生などから事務局に届いた問い合わせの一覧です。グループメッセージや一斉連絡を含むすべてのやり取りは「すべてのやり取り」で見られます。"
      />
      <MessagePeopleFilters
        values={draftFilters}
        onChange={setDraftFilters}
        onSearch={() => setAppliedFilters({ ...draftFilters })}
      />
      <ListTable
        caption="共有受信箱"
        rows={query.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        state={state}
        errorMessage="共有受信箱の取得に失敗しました。"
        onRetry={() => void query.refetch()}
        emptyTitle="該当する問い合わせはありません"
      />
    </>
  );
}
